import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Send } from 'lucide-react';
import { z } from 'zod';
import { trackEvent } from '@/components/GoogleAnalytics';
import { secureLog } from '@/lib/security';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  section: {
    id: string;
    section_name: string;
    section_type: string;
    target_date: string | null;
    countdown_enabled: boolean;
  };
  phases: Array<{
    id: string;
    phase_title: string;
    display_order: number;
  }>;
  items: Array<{
    id: string;
    item_text: string;
    phase_id: string | null;
    display_order: number;
    is_ready: boolean;
    is_bundled: boolean;
    is_secured: boolean;
    notes: string | null;
  }>;
}

const shareSchema = z.object({
  email: z.string().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  message: z.string().max(500, 'Message must be less than 500 characters').optional(),
});

export const ShareModal = ({ isOpen, onClose, section, phases, items }: ShareModalProps) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('I thought you might find this triathlon checklist helpful!');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      trackEvent('share_modal_opened', { user_authenticated: !!user });
    }
  }, [isOpen, user]);

  const handleShare = async () => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to share checklists.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Validate input
      const validatedData = shareSchema.parse({ email: email.trim(), message: message.trim() });
      
      setIsLoading(true);

      // Test basic auth by checking if user can read their own sections
      const { data: testData, error: testError } = await supabase
        .from('checklist_sections')
        .select('id, section_name')
        .eq('user_id', user.id)
        .limit(1);
        
      if (testError) {
        secureLog.error('ShareModal: Database access test failed:', testError);
        throw new Error('Database access failed: ' + testError.message);
      }

      // Generate secure share token with hash
      const { data: tokenData, error: tokenError } = await supabase.rpc('generate_share_token_with_hash');
      
      if (tokenError || !tokenData) {
        secureLog.error('ShareModal: Token generation error:', tokenError);
        throw new Error('Failed to generate share token: ' + tokenError.message);
      }

      const tokenObject = typeof tokenData === 'string' ? JSON.parse(tokenData) : tokenData;
      const shareToken = tokenObject.token;
      const tokenHash = tokenObject.hash;

      // Create share record (email not stored - only used for notification)
      const { data: shareData, error: shareError } = await supabase
        .from('checklist_shares')
        .insert({
          section_id: section.id,
          sender_id: user.id,
          share_token: shareToken,
          token_hash: tokenHash,
        })
        .select()
        .single();

      if (shareError) {
        secureLog.error('ShareModal: Share record creation error:', shareError);
        throw new Error('Failed to create share record');
      }

      // Store section data for sharing
      const { error: dataError } = await supabase
        .from('shared_section_data')
        .insert({
          share_id: shareData.id,
          section_name: section.section_name,
          section_type: section.section_type,
          target_date: section.target_date,
          countdown_enabled: section.countdown_enabled,
          phases: phases.map(phase => ({
            id: phase.id,
            phase_title: phase.phase_title,
            display_order: phase.display_order,
          })),
          items: items.map(item => ({
            id: item.id,
            item_text: item.item_text,
            phase_id: item.phase_id,
            display_order: item.display_order,
            is_ready: item.is_ready,
            is_bundled: item.is_bundled,
            is_secured: item.is_secured,
            notes: item.notes,
          })),
        });

      if (dataError) {
        secureLog.error('ShareModal: Shared data storage error:', dataError);
        throw new Error('Failed to store shared data');
      }

      // Get sender's full name from user metadata
      const senderFullName = user.user_metadata?.full_name || user.email || 'Anonymous User';

      // Send email notification
      const { error: emailError } = await supabase.functions.invoke('send-share-notification', {
        body: {
          recipientEmail: validatedData.email,
          senderName: senderFullName,
          senderEmail: user.email,
          sectionName: section.section_name,
          shareToken: shareToken,
          personalMessage: validatedData.message || '',
        },
      });

      if (emailError) {
        secureLog.error('ShareModal: Email sending failed:', emailError);
        // Don't throw here - the share was created successfully
      }

      // Log audit action with secure logging (no email exposure)
      const { error: auditError } = await supabase.rpc('log_share_action_secure', {
        _share_id: shareData.id,
        _action_type: 'created',
        _token_hash: tokenHash,
        _attempt_result: 'success',
        _security_notes: { section_name: section.section_name },
        _user_agent: navigator.userAgent,
      });
        
      if (auditError) {
        secureLog.error('ShareModal: Audit logging failed:', auditError);
        // Don't throw here - not critical for functionality
      }

      // Track share event WITHOUT email (security improvement)
      trackEvent('checklist_shared', { 
        user_authenticated: !!user,
        share_id: shareData.id
      });

      toast({
        title: 'Checklist shared successfully',
        description: `Your "${section.section_name}" checklist has been shared with ${validatedData.email}.`,
      });

      // Reset form and close modal
      setEmail('');
      setMessage('I thought you might find this triathlon checklist helpful!');
      onClose();

    } catch (error) {
      secureLog.error('Share error:', error);
      
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast({
          title: 'Invalid input',
          description: firstError.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Failed to share checklist',
          description: 'Please try again later.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display font-semibold">
            <Send className="h-5 w-5" />
            Share "{section.section_name}"
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="email" className="font-body">Recipient Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="friend@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="mt-1 font-body"
            />
          </div>
          
          <div>
            <Label htmlFor="message" className="font-body">Personal Message (Optional)</Label>
            <Textarea
              id="message"
              placeholder="I thought you might find this triathlon checklist helpful!"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading}
              className="mt-1 font-body"
              rows={3}
              maxLength={500}
            />
            <div className={`text-sm text-right mt-1 ${message.length >= 450 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {message.length}/500 characters
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleShare}
              disabled={isLoading || !email.trim()}
              variant="accent"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sharing...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Share Checklist
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};